import type { NativePendingIncomingCall } from '../../../services/voice-call/native-call'
import type { PublishVoiceCallSignalInput } from '../../../types/voice-call'

/**
 * Why a natively-answered call (Telecom / CallKit / notification Answer) could
 * not be turned into a JS call session:
 *  - `notCallee`: the invite names a different callee than the signed-in user
 *  - `selfCaller`: the invite names the signed-in user as the caller
 *  - `missingIds`: the seeded invite has no caller/callee ids
 *  - `busy`: this device is already on a different call
 *  - `rebuildFailed`: building the session threw (e.g. invalid caller)
 */
export type NativeAnswerAbortReason =
  | 'notCallee'
  | 'selfCaller'
  | 'missingIds'
  | 'busy'
  | 'rebuildFailed'

export type NativeAnswerAbortDeps = {
  publish: (input: PublishVoiceCallSignalInput) => Promise<unknown>
  disconnectNative: (callId: string) => Promise<void> | void
  clearNotification?: (callId: string) => void
}

/**
 * The signal that settles a call this device answered natively but cannot
 * join. The OS answer may already have relayed an ACCEPT (native ack), so the
 * caller would otherwise sit connected to silence: a 1:1 call is ENDed (BUSY
 * when we are on another call, INTERRUPTED otherwise); a room invite is
 * REJECTed like a Decline. Null when the invite lacks the party ids the
 * server needs, and for `notCallee` / `selfCaller`: the signed-in user is not
 * the callee of that call, so a signal from them would at best be refused
 * ("Not a participant") and at worst end a call between other people.
 */
export function nativeAnswerAbortSignal(
  invite: NativePendingIncomingCall,
  reason: NativeAnswerAbortReason,
  nowMs: number = Date.now(),
): PublishVoiceCallSignalInput | null {
  if (reason === 'notCallee' || reason === 'selfCaller') return null
  if (!invite.callerId || !invite.calleeId) return null
  const callId = invite.joinCallId || invite.callId
  const base = {
    callId,
    callerId: invite.callerId,
    calleeId: invite.calleeId,
    conversationId: invite.conversationId ?? null,
  }
  if (invite.roomInvite) return { ...base, type: 'REJECT', roomInvite: true, joinCallId: callId }
  const at = new Date(nowMs).toISOString()
  return {
    ...base,
    type: 'END',
    payload: {
      reason: reason === 'busy' ? 'BUSY' : 'INTERRUPTED',
      detail: `native-answer-${reason}`,
      startedAt: at,
      endedAt: at,
      mediaType: invite.mediaType,
    },
  }
}

/**
 * Settle a native answer that has no JS session behind it: tell the server
 * (so the caller hears the call end instead of silence) and tear down the
 * native connection / notification on this device. Never throws.
 */
export async function abortUnbuildableNativeAnswer(
  invite: NativePendingIncomingCall,
  reason: NativeAnswerAbortReason,
  deps: NativeAnswerAbortDeps,
): Promise<void> {
  const callIds = Array.from(new Set([invite.callId, invite.joinCallId].filter(Boolean))) as string[]
  for (const id of callIds) {
    deps.clearNotification?.(id)
    try {
      await deps.disconnectNative(id)
    } catch {
      /* nothing to disconnect */
    }
  }
  const signal = nativeAnswerAbortSignal(invite, reason)
  if (!signal) return
  await deps.publish(signal).catch(() => {})
}
